// SPDX-FileCopyrightText: 2026 Ebiose AI
// SPDX-License-Identifier: LicenseRef-Ebiose-Proprietary
// All rights reserved. Confidential — unauthorized distribution prohibited.

import { useEffect, useId, useRef } from "react"
import {
  buildMermaidIdMap,
  INPUT_ID,
  OUTPUT_ID,
} from "@/lib/mermaid-generator"

// Module-level guard so `mermaid.initialize` runs exactly once across every
// mount (PRD: "initialized once"). The dynamic import is cached by the bundler;
// this flag de-duplicates the configuration call.
let mermaidInitialized = false

export interface MermaidDiagramProps {
  definition: string
  nodes: NodeModel[]
  selectedNodeId: string | null
  onNodeClick: (nodeId: string) => void
  onError: () => void
}

async function loadMermaid() {
  const mermaid = (await import("mermaid")).default
  if (!mermaidInitialized) {
    mermaid.initialize({
      startOnLoad: false,
      securityLevel: "strict",
      // Visual configuration from the Lagon design system (mockup
      // `Détail Agent.dc.html` §renderGraph) — the visual spec of record
      // for the graph zone: ink canvas, teal node chrome, muted lines.
      theme: "base",
      fontFamily: "'Sora Variable', Sora, ui-sans-serif, sans-serif",
      themeVariables: {
        background: "#07191D",
        primaryColor: "#0B2A30",
        primaryTextColor: "#D9F2EA",
        primaryBorderColor: "#2E6E72",
        lineColor: "#2E6E72",
        secondaryColor: "#07191D",
        tertiaryColor: "#07191D",
        edgeLabelBackground: "#07191D",
        fontSize: "14px",
      },
      flowchart: { curve: "basis", nodeSpacing: 45, rankSpacing: 50 },
    })
    mermaidInitialized = true
  }
  return mermaid
}

export function MermaidDiagram({
  definition,
  nodes,
  selectedNodeId,
  onNodeClick,
  onError,
}: MermaidDiagramProps) {
  const containerRef = useRef<HTMLDivElement | null>(null)
  // Listeners attached on the current render, torn down before the next.
  const cleanupRef = useRef<(() => void) | null>(null)
  // Per-instance Mermaid render id (DOM-id-safe) so two live instances
  // (e.g. inline card + fullscreen overlay) never collide.
  const instanceId = `agentGraphSvg_${useId().replace(/[^A-Za-z0-9_]/g, "")}`
  // Monotonic per-render suffix: the id passed to `mermaid.render` must
  // never match an element already in the DOM — the mounted SVG keeps
  // the previous render's id (its embedded stylesheet is scoped to it),
  // and Mermaid renders INTO any live element whose id it is given
  // (transient duplicate diagram on re-render).
  const renderSeq = useRef(0)

  useEffect(() => {
    let cancelled = false

    // Map Mermaid id → original node id by inverting the same
    // collision-free map the generator used, so a clicked SVG node
    // always resolves to the right original node. Only concrete nodes
    // are present (endpoints aren't passed in), so any id absent from
    // this map is treated as non-clickable.
    const idToNode = new Map<string, string>()
    for (const [originalId, mermaidId] of buildMermaidIdMap(nodes)) {
      idToNode.set(mermaidId, originalId)
    }

    async function renderDiagram() {
      const container = containerRef.current
      if (!container) return

      let svg: string
      try {
        const mermaid = await loadMermaid()
        const result = await mermaid.render(
          `${instanceId}_${renderSeq.current++}`,
          definition,
        )
        svg = result.svg
      } catch {
        // Contain the failure: never let it escape, signal the parent so
        // the page can swap to the degraded text-list branch.
        if (!cancelled) onError()
        return
      }
      if (cancelled) return

      container.innerHTML = svg
      // Prototype sizing contract (`wireGraph`): the diagram fills the
      // canvas height and keeps its aspect ratio, capped at the canvas
      // width — never the small natural-size render pinned in a corner.
      const svgEl = container.querySelector("svg")
      if (svgEl) {
        svgEl.style.height = "100%"
        svgEl.style.maxWidth = "100%"
        svgEl.style.width = "auto"
        // The node selection/hover neon glow is a `drop-shadow` filter that
        // extends past the SVG's content-box viewport. An SVG clips to its
        // viewport by default, shearing the glow ("box du graphe cropé") —
        // let it bleed out so the surrounding padding can hold it.
        svgEl.style.overflow = "visible"
      }
      // Routing-key labels read as annotations, not peers of the node
      // names — render them one notch smaller (node text stays 14px).
      container
        .querySelectorAll<HTMLElement | SVGElement>(".edgeLabel")
        .forEach((el) => {
          el.style.fontSize = "11px"
        })
      instrument(container)
    }

    function instrument(container: HTMLElement) {
      // Tear down any listeners from a previous render first.
      cleanupRef.current?.()

      const detachers: (() => void)[] = []
      const nodeEls = container.querySelectorAll<SVGGElement>(".node")
      nodeEls.forEach((el) => {
        const match = (el.id || "").match(/flowchart-(.+?)-\d+$/)
        const mermaidId = match ? match[1] : null
        const isEndpoint = el.classList.contains("io")
        const originalId = mermaidId ? idToNode.get(mermaidId) : undefined

        // The selection identity this element resolves to:
        //  - concrete node  → its original wire id (via the id map)
        //  - io endpoint    → the synthetic `input` / `output` id, which
        //    is also its Mermaid id, so the inspector can map it to the
        //    agent-level input / output schema
        //  - anything else  → inert (no identity)
        const selectionId = isEndpoint
          ? mermaidId === INPUT_ID || mermaidId === OUTPUT_ID
            ? mermaidId
            : null
          : (originalId ?? null)

        // Selection highlight — identical for EVERY node type. Mermaid 11
        // draws the border on different element kinds per shape (a `<rect>`
        // for the rectangular LLM / gate nodes, a `<path>` for the stadium
        // input / output endpoints), so we style *all* of a node's border
        // shapes, not just the first match — otherwise the mint selection
        // ring lands on the fill of one shape and the border of another and
        // looks inconsistent between endpoints and regular nodes.
        // Mermaid's `classDef` emits the node border (`stroke` /
        // `stroke-width`) as `!important` CSS on the `.llm` / `.gate`
        // classes, so a plain inline style is ignored on the rectangular
        // nodes — only the `io` endpoints (whose class sets no
        // `stroke-width`) would visibly react. Set the selection styles
        // with `important` priority so the mint ring beats the classDef on
        // EVERY shape, then remove the override on deselect.
        const selected = selectionId != null && selectionId === selectedNodeId
        el.querySelectorAll<SVGElement>("rect, polygon, path, circle").forEach(
          (shape) => {
            if (selected) {
              shape.style.setProperty("stroke", "#45F0C8", "important")
              shape.style.setProperty("stroke-width", "1.5px", "important")
              shape.style.setProperty(
                "filter",
                "drop-shadow(0 0 9px rgba(69,240,200,0.55))",
                "important",
              )
            } else {
              shape.style.removeProperty("stroke")
              shape.style.removeProperty("stroke-width")
              shape.style.removeProperty("filter")
            }
          },
        )

        // Unidentifiable elements are inert.
        if (selectionId == null) return

        el.style.cursor = "pointer"
        const handler = () => onNodeClick(selectionId)
        el.addEventListener("click", handler)
        detachers.push(() => el.removeEventListener("click", handler))
      })

      cleanupRef.current = () => {
        for (const detach of detachers) detach()
        cleanupRef.current = null
      }
    }

    void renderDiagram()

    return () => {
      cancelled = true
      cleanupRef.current?.()
    }
  }, [definition, nodes, selectedNodeId, onNodeClick, onError, instanceId])

  return (
    <div
      ref={containerRef}
      data-testid="mermaid-diagram"
      className="flex h-full w-full items-center justify-center p-12"
    />
  )
}
